import React, {
  ComponentType,
  Suspense,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { usePortals } from '@molecules/BEPortal';
import { PopoverProps } from './Popover';
import { ModalProps } from './Modal';

type OffScreenComponentProps<C, P> = C extends ComponentType<P & infer U>
  ? U
  : never;

type OffScreenInstance = { close: () => void };

export type UseOffScreenHookProps<P> = Omit<
  P,
  'onOk' | 'onCancel' | 'buttons'
> & {
  onOk?: (instance: OffScreenInstance) => void;
  onCancel?: (instance: OffScreenInstance) => void;
};

const useOffScreen = <
  P extends PopoverProps | ModalProps,
  C extends ComponentType<any>,
  OpenProps extends OffScreenComponentProps<C, P>
>(
  Component: C,
  OffScreenProps: UseOffScreenHookProps<P>
) => {
  const { id, ...offscreenProps } = OffScreenProps;
  const { open: openPortal, close: closePortal, has } = usePortals();
  const [tag, setTag] = useState(undefined);
  const [currentProps, setCurrentProps] =
    useState<UseOffScreenHookProps<P>>(OffScreenProps);
  const offscreenId = id || 'offscreen';

  const { onOk, onCancel, afterClose, ...props } = offscreenProps || {};

  const onCancelProxy = useCallback(
    (portal: OffScreenInstance) =>
      onCancel ? () => onCancel(portal) : portal.close,
    [onCancel]
  );

  const onOkProxy = useCallback(
    (portal: OffScreenInstance) => async () => {
      await (
        onOk as ((portal: OffScreenInstance) => Promise<void>) | undefined
      )?.(portal);
      portal.close();
    },
    [onOk]
  );

  const portal = useMemo(
    () => ({ close: () => closePortal(offscreenId) }),
    [offscreenId, closePortal]
  );

  return useMemo(
    () => ({
      open: (openProps?: OpenProps & { tag?: string }) => {
        setTag(openProps?.tag);
        setCurrentProps({ id: offscreenId, ...OffScreenProps });
        openPortal(
          <Suspense fallback={null}>
            {/* @ts-expect-error @todo: fix emotion type error */}
            <Component
              onCancel={onCancelProxy(portal)}
              onOk={onOkProxy(portal)}
              {...openProps}
              {...props}
            />
          </Suspense>,
          {
            id: offscreenId,
            onClose: () => {
              afterClose?.();
            },
          }
        );
      },
      close: portal.close,
      isOpened: () => has(offscreenId),
      tag,
      props: currentProps,
    }),
    [
      openPortal,
      OffScreenProps,
      Component,
      afterClose,
      offscreenId,
      onCancelProxy,
      onOkProxy,
      currentProps,
      portal,
      has,
      tag,
      props,
    ]
  );
};

export const useModal = <
  C extends ComponentType<any>,
  O extends OffScreenComponentProps<C, ModalProps>
>(
  Component: C,
  Props: UseOffScreenHookProps<ModalProps>
) => {
  return useOffScreen<ModalProps, C, O>(Component, Props);
};

export const usePopover = <
  C extends ComponentType<any>,
  O extends OffScreenComponentProps<C, PopoverProps>
>(
  Component: C,
  Props: UseOffScreenHookProps<PopoverProps>
) => {
  return useOffScreen<PopoverProps, C, O>(Component, Props);
};
